import { slugArrayToSlug } from "@/app/(utils)/page"
import { MediaLink } from "../media/types"

export interface PageFileStub {
  path: string
  slug: string
  pageType: PageType
  /** The slug of the parent page, if any */
  parentSlug?: string
  /** The slugs of the child pages, if any */
  childSlugs?: string[]
}

export interface Page {
  /** The type of page, mandatory */
  pageType: Omit<PageType, PageType.ALL>
  /** The slug used in routes e.g.:
   * - "knowledge/the-solution"
   * - "knowledge/plan-build-run/plan-phase"
   * - "team/john-doe"
   */
  slug: string
  /** The main title of the page */
  title: string
  /** Optional subtitle, shown under the main title when available */
  subtitle?: string
  /** Whether this page should be published (default: true) */
  publish?: boolean
  /** The optional date of the page, used for sorting and filtering */
  date?: string
  /** The last modified date of the page file */
  lastModified?: string
  /** Optional hero section variant for the page */
  heroVariant?: HeroVariantType
  /** Slugs of related knowledge pages */
  relatedPages?: string[]
  /** Optional order for controlling display order */
  order?: number
}

export interface PageWithContent extends Page {
  content: string
}

/**
 * Represents a page that belongs to a parent (used for nested structures
 * such as Knowledge pages).
 */
export interface ChildPage extends Page {
  /** The full slash-separated slug of the parent page, if any */
  parentSlug?: string
}

/**
 * Represents a parent-level page that can have children.
 */
export interface ParentPage extends Page {
  /** The full slash-separated slugs of the child pages, if any */
  childSlugs?: string[]
}

/**
 * Adds an optional hero/featured image to a page.
 */
export interface PageWithMedia extends Page {
  image?: string
  mediaLinks?: MediaLink[]
}

/**
 * Adds the capability to a page to have tags.
 */
export interface PageWithTags extends Page {
  tags?: string[]
}

export enum InsightType {
  ARTICLE = "article",
  PODCAST = "podcast",
  EVENT = "event",
  ALL = "all",
}

export interface PageWithInsightType extends Page {
  insightType: Omit<InsightType, InsightType.ALL>
}

export const getPathForInsightType = (type: InsightType): string => {
  if (type === InsightType.ALL) return ""
  if (type === InsightType.ARTICLE) return "articles"
  if (type === InsightType.PODCAST) return "podcasts"
  if (type === InsightType.EVENT) return "events"
  throw new Error(`Unknown InsightType: ${type}`)
}

export const getInsightTypeFromSlug = (slug: string): InsightType => {
  let lowerSlug = slug.toLowerCase().replace(/^\//, "")
  if (!lowerSlug.endsWith("/")) lowerSlug += "/"
  if (lowerSlug === "insights/") return InsightType.ALL
  lowerSlug += "whatever"

  for (const type of Object.values(InsightType)) {
    if (lowerSlug.startsWith(`insights/${getPathForInsightType(type)}/`))
      return type
  }

  throw new Error(`Unknown InsightType for slug: ${slug}`)
}

export enum PageType {
  LEGAL = "legal",
  KNOWLEDGE = "knowledge",
  INSIGHT = "insight",
  PARTNER = "partner",
  TEAM = "team",
  ALL = "all",
}

export async function getPageDirectory(page: Page): Promise<string> {
  return await getDirectoryForPageType(page.pageType as PageType)
}

export function getPageDirectoryForAllContent(): string {
  return "content"
}

export async function getDirectoryForPageType(
  pageType: PageType
): Promise<string> {
  const contentDir = await getPageDirectoryForAllContent()
  if (pageType === PageType.ALL) {
    return contentDir
  }
  if (pageType === PageType.LEGAL) {
    return `${contentDir}/legal`
  }
  if (pageType === PageType.KNOWLEDGE) {
    return `${contentDir}/knowledge`
  }
  if (pageType === PageType.INSIGHT) {
    return `${contentDir}/insights`
  }
  if (pageType === PageType.PARTNER) {
    return `${contentDir}/partners`
  }
  if (pageType === PageType.TEAM) {
    return `${contentDir}/team`
  }
  throw new Error(`Unknown page type: ${pageType}`)
}

export async function getAllContentDirectories(): Promise<
  [PageType, string][]
> {
  return Promise.all(
    Object.values(PageType).map(async pageType => [
      pageType as PageType,
      await getDirectoryForPageType(pageType as PageType),
    ])
  )
}

export function getBaseUrl(page: Page): string {
  return getBaseUrlForType(page.pageType as PageType)
}

export function getBaseUrlForType(pageType: PageType): string {
  if (pageType === PageType.ALL) {
    return ""
  }
  if (pageType === PageType.LEGAL) {
    return "/legal"
  }
  if (pageType === PageType.KNOWLEDGE) {
    return "/knowledge"
  }
  if (pageType === PageType.INSIGHT) {
    return "/insights"
  }
  if (pageType === PageType.PARTNER) {
    return "/partners"
  }
  if (pageType === PageType.TEAM) {
    return "/team"
  }
  throw new Error(`Unknown page type: ${pageType}`)
}

export const slugArrayToSlugForType = (
  pageType: PageType,
  slug: string[]
): string => {
  const base = getBaseUrlForType(pageType).replace(/^\//, "")
  const joined = slugArrayToSlug(slug)
  return base ? `${base}/${joined}` : joined
}

export function getFullSlug(page: Page): string {
  return getSlugForType(page.pageType as PageType, page.slug)
}

export function getSlugForType(
  pageType: PageType,
  slug: string | undefined
): string {
  if (slug === undefined || slug === "index" || slug === "") {
    return getBaseUrlForType(pageType).replace(/\/$/, "")
  }
  return slug
}

export async function getPageTypeFromSlug(slug: string): Promise<PageType> {
  const fixedSlug = fixSlug(slug)
  if (!fixedSlug) return PageType.ALL
  const checkSlug = `${fixedSlug}/`
  if (checkSlug.startsWith("/insights/")) return PageType.INSIGHT
  if (checkSlug.startsWith("/legal/")) return PageType.LEGAL
  if (checkSlug.startsWith("/knowledge/")) return PageType.KNOWLEDGE
  if (checkSlug.startsWith("/partners/")) return PageType.PARTNER
  if (checkSlug.startsWith("/team/")) return PageType.TEAM
  throw new Error(`Unknown page type for slug: ${checkSlug}`)
}

// Variants for the hero section
export enum HeroVariantType {
  DEFAULT = "default",
  ILLUSTRATION = "illustration",
  MINIMAL = "minimal",
}

export function fixSlug(slug: string): string | undefined {
  if (!slug) return undefined
  if (slug === "") return undefined
  // This should never happen, but just in case, we'll throw an error
  // because nextjs pages receive slugs as arrays that may have
  // accidentally been passed to fixSlug
  if (Array.isArray(slug)) {
    throw new Error(`fixSlug: slug is an array: ${slug}`)
  }
  if (slug.endsWith("/")) {
    slug = slug.slice(0, -1)
  }
  if (!slug.startsWith("/") && slug !== "") {
    slug = `/${slug}`
  }
  return slug.toLowerCase()
}
